import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import API from '../../../../api/api'
import { formatMoney } from '../../../../helpers/helpers'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import Loader from '../../../Loader/Loader'

import ParkingPaymentElement from './ParkingPaymentElement'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY)

const ParkingPaymentForm = ({ ticketNumber, isLoading, serviceFee, setStage, requestTips }) => {
  const { t } = useTranslation(['parking', 'common'])
  const [addTip, setAddTip] = useState(false)
  const [tip, setTip] = useState(0)
  const [isError, setIsError] = useState(false)
  const navigate = useNavigate()

  const totalPrice = formatMoney((serviceFee || 0) / 100 + (tip || 0))

  const totalAmount = (tip || 0) * 100 + serviceFee
  const options = useMemo(
    () => ({ mode: 'payment', amount: totalAmount, currency: 'usd' }),
    [totalAmount],
  )

  const handleRequestVehicle = async () => {
    setIsError(false)
    try {
      await API.requestVehicle(0, ticketNumber)
      navigate(`/parking/${ticketNumber}/summary`)
    } catch (error) {
      console.error(error)
      setIsError(true)
    }
  }

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (!serviceFee && !requestTips) {
      handleRequestVehicle()
    }
  }, [serviceFee, requestTips, isLoading])

  const renderCostService = () => {
    if (!serviceFee) {
      return null
    }

    return (
      <React.Fragment>
        <div className="parking-info__section-title">{t('parking:paymentForm.serviceCost')}</div>
        <div className="parking-info__section-info-full-row">
          <div>{t('parking:paymentForm.serviceCost')}</div>
          <div className="service-cost">
            {formatMoney(serviceFee / 100) || t('parking:servicesSummary.na')}
          </div>
        </div>
      </React.Fragment>
    )
  }

  const renderTipForm = () => {
    if (!requestTips) {
      return null
    }

    return (
      <React.Fragment>
        <div className="tip-container">
          <div className="title">{t('parking:paymentForm.addTipTitle')}</div>
          <div className="tip-options">
            <button
              onClick={() => setTip(3)}
              className={`button ${tip === 3 ? 'button-black' : 'button-grey'} flex-1`}
            >
              $3
            </button>
            <button
              onClick={() => setTip(5)}
              className={`button ${tip === 5 ? 'button-black' : 'button-grey'} flex-1`}
            >
              $5
            </button>
            <button
              onClick={() => setTip(10)}
              className={`button ${tip === 10 ? 'button-black' : 'button-grey'} flex-1`}
            >
              $10
            </button>
          </div>
        </div>
        <div className="custom-tip">
          <div className="flex-row">
            <div className="tip-column" />
            <div className="tip-column">{t('parking:paymentForm.customValuePrompt')}</div>
          </div>
          <div className="flex-row">
            <div className="tip-column tip-label">{t('parking:paymentForm.tipLabel')}</div>
            <div className="tip-column">
              <TextField
                type="number"
                onChange={(e) => setTip(parseInt(e.target.value))}
                value={tip}
                name="tip"
              />
            </div>
          </div>
        </div>
      </React.Fragment>
    )
  }

  const renderTotal = () => {
    return (
      <React.Fragment>
        <div className="parking-info__section-separator" />
        <div className="parking-info__section-row">
          <div className="text">{t('parking:paymentForm.total')}</div>
          <div className="text">{totalPrice}</div>
        </div>
      </React.Fragment>
    )
  }

  const renderAddTipQuestion = () => {
    return (
      <React.Fragment>
        <div className="tip-question">{t('parking:paymentForm.tipQuestion')}</div>
        <div className="tip-answers">
          <button onClick={handleRequestVehicle} className={`button ${!addTip && 'is-black'}`}>
            {t('common:booleans.no')}
          </button>
          <button onClick={() => setAddTip(true)} className={`button ${addTip && 'is-black'}`}>
            {t('common:booleans.yes')}
          </button>
        </div>
        {isError && <div>{t('parking:paymentForm.errorProcessingRequest')}</div>}
      </React.Fragment>
    )
  }

  const renderInitialScreen = () => {
    if (isLoading) {
      return <Loader />
    }

    if (!serviceFee && !requestTips) {
      if (isError) {
        return <div>{t('parking:paymentForm.errorProcessingRequest')}</div>
      }

      return <Loader />
    }

    if (!serviceFee && requestTips) {
      if (!addTip) {
        return renderAddTipQuestion()
      }

      return (
        <div className="parking-info__section">
          {renderCostService()}
          {renderTipForm()}
          {renderTotal()}
        </div>
      )
    }

    return (
      <div className="parking-info__section">
        {renderCostService()}
        {renderTipForm()}
        {renderTotal()}
      </div>
    )
  }

  return (
    <React.Fragment>
      {renderInitialScreen()}
      {serviceFee || tip ? (
        <Elements stripe={stripePromise} options={options}>
          <ParkingPaymentElement
            serviceFee={serviceFee}
            setStage={setStage}
            ticketNumber={ticketNumber}
            tip={tip}
            totalPrice={totalPrice}
          />
        </Elements>
      ) : null}
    </React.Fragment>
  )
}

ParkingPaymentForm.propTypes = {
  tip: PropTypes.number,
  serviceFee: PropTypes.number,
  ticketNumber: PropTypes.string,
  isLoading: PropTypes.bool,
  requestTips: PropTypes.bool,
  setStage: PropTypes.func,
  setTip: PropTypes.func,
}

export default ParkingPaymentForm
